import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, ChevronRight, Star, Gem, Award, Sparkles, Plus, Heart, Truck, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 3: PRESTIGE
 * Style: Luxury, Elegant, Sophisticated
 * Best for: High-end fashion, Designer brands, Premium boutiques
 * Inspired by: Shopify Prestige theme
 */
const Template3 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    const testimonials = [
        {
            text: "The attention to detail and quality of every piece I've purchased has been extraordinary. This is what true luxury fashion should be.",
            name: 'Alexandra Chen',
            location: 'Fashion Editor, Vogue',
            rating: 5
        },
        {
            text: "Impeccable craftsmanship meets modern design. Each garment feels like it was made exclusively for me. Truly exceptional.",
            name: 'Priya Kapoor',
            location: 'Mumbai',
            rating: 5
        },
        {
            text: "I have never experienced such thoughtful design and quality materials. The silk collection is absolutely divine.",
            name: 'Sophia Laurent',
            location: 'Paris',
            rating: 5
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial(prev => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [testimonials.length]);

    const handleSwipe = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            if (diff > 0) setActiveTestimonial(prev => (prev + 1) % testimonials.length);
            else setActiveTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const categories = [
        { name: 'Dresses', image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=600&h=800&fit=crop' },
        { name: 'Blazers', image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=600&h=800&fit=crop' },
        { name: 'Accessories', image: 'https://images.unsplash.com/photo-1611085583191-a3b181a88401?w=600&h=800&fit=crop' }
    ];

    const products = [
        { name: 'Silk Midi Dress', price: '\u20B924,999', originalPrice: null, badge: 'NEW', image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=500&h=650&fit=crop' },
        { name: 'Cashmere Blazer', price: '\u20B932,999', originalPrice: null, badge: null, image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=500&h=650&fit=crop' },
        { name: 'Leather Tote', price: '\u20B918,999', originalPrice: '\u20B924,999', badge: 'SALE', image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=500&h=650&fit=crop' },
        { name: 'Pearl Earrings', price: '\u20B98,999', originalPrice: null, badge: null, image: 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=500&h=650&fit=crop' },
        { name: 'Wool Coat', price: '\u20B945,999', originalPrice: null, badge: 'BESTSELLER', image: 'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=500&h=650&fit=crop' },
        { name: 'Satin Blouse', price: '\u20B912,999', originalPrice: null, badge: 'NEW', image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=650&fit=crop' },
        { name: 'Heeled Sandals', price: '\u20B915,999', originalPrice: null, badge: null, image: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=500&h=650&fit=crop' },
        { name: 'Evening Clutch', price: '\u20B99,999', originalPrice: null, badge: null, image: 'https://images.unsplash.com/photo-1566150905458-1bf1fc113f0d?w=500&h=650&fit=crop' }
    ];

    const features = [
        { icon: Sparkles, title: 'Personal Styling', desc: 'One-on-one expert consultations' },
        { icon: Award, title: 'Premium Quality', desc: 'Finest materials & craftsmanship' },
        { icon: Gem, title: 'Exclusive Access', desc: 'First access to new collections' },
        { icon: Star, title: 'White Glove Delivery', desc: 'Luxury packaging & delivery' }
    ];

    const getBadgeColor = (badge) => {
        switch (badge) {
            case 'NEW': return 'bg-stone-900';
            case 'SALE': return 'bg-amber-700';
            case 'BESTSELLER': return 'bg-stone-700';
            default: return 'bg-stone-500';
        }
    };

    return (
        <div className="min-h-screen bg-stone-50 text-stone-900" style={{ fontFamily: 'Georgia, serif' }}>

            {/* ============================================
                HERO SECTION
            ============================================ */}

            {/* Mobile Hero - stacked layout */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5]">
                    <img
                        src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80"
                        alt="Luxury Fashion"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="bg-stone-50 px-5 py-8">
                    <p className="text-stone-400 tracking-[0.15em] uppercase text-[11px] mb-3">The Autumn Collection</p>
                    <h1 className="text-[1.75rem] leading-[1.15] font-light text-stone-900 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                        Timeless Elegance, <span className="italic">Redefined</span>
                    </h1>
                    <p className="text-stone-500 text-[14px] leading-relaxed mb-6 max-w-lg">
                        Discover our curated collection of exceptional pieces, crafted for those who appreciate the finest in fashion.
                    </p>
                    <button
                        onClick={() => navigate('/template3/shop')}
                        className="group w-full inline-flex items-center justify-center gap-3 bg-stone-900 text-white px-8 py-4 min-h-[48px] text-[12px] tracking-widest uppercase hover:bg-stone-800 transition-all active:scale-[0.98]"
                    >
                        Explore Collection
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </section>

            {/* Desktop/Tablet Hero - Full editorial with elegant overlay (ORIGINAL) */}
            <section className="hidden lg:flex relative min-h-screen items-end">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80"
                        alt="Luxury Fashion"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/30 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 pb-16 lg:pb-24">
                    <div className="max-w-2xl">
                        <p className="text-stone-300 tracking-[0.3em] uppercase text-sm mb-4">
                            The Autumn Collection
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-7xl text-white font-light leading-tight mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                            Timeless Elegance,
                            <br />
                            <span className="italic">Redefined</span>
                        </h1>
                        <p className="text-stone-300 text-lg mb-8 max-w-lg leading-relaxed">
                            Discover our curated collection of exceptional pieces, crafted for those who appreciate the finest in fashion.
                        </p>
                        <button
                            onClick={() => navigate('/template3/shop')}
                            className="group inline-flex items-center gap-4 bg-white text-stone-900 px-8 py-4 text-sm tracking-widest uppercase hover:bg-stone-100 transition-all duration-300"
                        >
                            Explore Collection
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center text-white/60">
                    <span className="text-xs tracking-widest uppercase mb-2">Scroll</span>
                    <div className="w-px h-12 bg-white/30 relative overflow-hidden">
                        <div className="w-full h-4 bg-white absolute animate-bounce"></div>
                    </div>
                </div>
            </section>

            {/* ============================================
                INTRO TEXT - Brand philosophy (shared responsive)
            ============================================ */}
            <section className="py-10 sm:py-16 lg:py-32 bg-stone-50">
                <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
                        <div className="w-8 sm:w-12 h-px bg-stone-400"></div>
                        <Gem className="w-4 h-4 sm:w-5 sm:h-5 text-stone-500" />
                        <div className="w-8 sm:w-12 h-px bg-stone-400"></div>
                    </div>
                    <h2 className="text-xl sm:text-2xl lg:text-5xl font-light leading-relaxed mb-6 sm:mb-8" style={{ fontFamily: 'Georgia, serif' }}>
                        "Where craftsmanship meets contemporary design, creating pieces that transcend seasons and trends."
                    </h2>
                    <p className="text-stone-500 tracking-[0.15em] sm:tracking-widest uppercase text-[11px] sm:text-xs lg:text-sm">
                        — The House of Prestige
                    </p>
                </div>
            </section>

            {/* ============================================
                FEATURED COLLECTIONS - Elegant dual cards (Desktop/Tablet only - ORIGINAL)
            ============================================ */}
            <section className="hidden lg:block py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16">
                    <div className="text-center mb-16">
                        <p className="text-stone-500 tracking-[0.3em] uppercase text-xs mb-4">Curated Selection</p>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light" style={{ fontFamily: 'Georgia, serif' }}>
                            Featured Collections
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                        {[
                            {
                                title: 'The Evening Edit',
                                subtitle: 'Effortless glamour for memorable nights',
                                image: 'https://images.unsplash.com/photo-1539008835657-9e8e9680c956?w=800&h=1000&fit=crop'
                            },
                            {
                                title: 'Day to Night',
                                subtitle: 'Versatile pieces for the modern woman',
                                image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&h=1000&fit=crop'
                            }
                        ].map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template3/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[4/5] overflow-hidden mb-6">
                                    <img
                                        src={collection.image}
                                        alt={collection.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/20 transition-colors duration-500"></div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-2xl lg:text-3xl font-light mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                                        {collection.title}
                                    </h3>
                                    <p className="text-stone-500 mb-4">{collection.subtitle}</p>
                                    <span className="inline-flex items-center gap-2 text-stone-900 text-sm tracking-widest uppercase border-b border-stone-900 pb-1 group-hover:border-stone-500 transition-colors">
                                        Shop Now <ChevronRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                CATEGORIES
            ============================================ */}

            {/* Mobile Categories - Horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16 bg-stone-100">
                <div className="px-5 sm:px-6 mb-6 sm:mb-10">
                    <p className="text-stone-500 tracking-[0.15em] uppercase text-[11px] sm:text-xs mb-2">Browse By</p>
                    <h2 className="text-xl sm:text-2xl font-light" style={{ fontFamily: 'Georgia, serif' }}>Categories</h2>
                </div>
                <div className="relative">
                    <div className="flex overflow-x-auto gap-3 px-5 pb-1 snap-x snap-mandatory scrollbar-hide sm:px-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template3/shop')}
                                className="flex-none w-[68vw] sm:w-[45vw] snap-start group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden rounded-2xl mb-3 sm:mb-4">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/20 transition-colors duration-500"></div>
                                </div>
                                <h3 className="text-center text-base sm:text-lg font-light tracking-wide group-hover:text-stone-600 transition-colors" style={{ fontFamily: 'Georgia, serif' }}>
                                    {category.name}
                                </h3>
                            </div>
                        ))}
                    </div>
                    <div className="absolute top-0 right-0 bottom-1 w-8 bg-gradient-to-l from-stone-100 to-transparent pointer-events-none" />
                </div>
            </section>

            {/* Desktop/Tablet Categories - Elegant horizontal scroll (ORIGINAL) */}
            <section className="hidden lg:block py-16 lg:py-24 bg-stone-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
                        <div>
                            <p className="text-stone-500 tracking-[0.3em] uppercase text-xs mb-4">Browse By</p>
                            <h2 className="text-3xl sm:text-4xl font-light" style={{ fontFamily: 'Georgia, serif' }}>
                                Categories
                            </h2>
                        </div>
                        <button
                            onClick={() => navigate('/template3/shop')}
                            className="inline-flex items-center gap-2 text-stone-600 text-sm tracking-widest uppercase hover:text-stone-900 transition-colors"
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="flex overflow-x-auto gap-6 pb-4 -mx-4 px-4 lg:mx-0 lg:px-0 scrollbar-hide snap-x snap-mandatory">
                        {[
                            { name: 'Dresses', image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=400&h=500&fit=crop' },
                            { name: 'Blazers', image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=400&h=500&fit=crop' },
                            { name: 'Accessories', image: 'https://images.unsplash.com/photo-1611085583191-a3b181a88401?w=400&h=500&fit=crop' },
                            { name: 'Shoes', image: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=400&h=500&fit=crop' },
                            { name: 'Bags', image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400&h=500&fit=crop' }
                        ].map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template3/shop')}
                                className="flex-none w-64 lg:w-72 group cursor-pointer snap-start"
                            >
                                <div className="relative aspect-[4/5] overflow-hidden mb-4">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <h3 className="text-center text-lg font-light tracking-wide group-hover:text-stone-600 transition-colors" style={{ fontFamily: 'Georgia, serif' }}>
                                    {category.name}
                                </h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                PRODUCTS
            ============================================ */}

            {/* Mobile Products - Horizontal scroll with wishlist/quick-add */}
            <section className="lg:hidden py-10 sm:py-16 bg-white">
                <div className="text-center px-5 sm:px-6 mb-6 sm:mb-10">
                    <p className="text-stone-500 tracking-[0.15em] uppercase text-[11px] sm:text-xs mb-3">New Arrivals</p>
                    <h2 className="text-xl sm:text-2xl font-light" style={{ fontFamily: 'Georgia, serif' }}>The Latest</h2>
                </div>

                <div className="relative">
                    <div className="flex overflow-x-auto gap-3 px-5 pb-2 snap-x snap-mandatory scrollbar-hide">
                        {products.slice(0, 6).map((product, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template3/shop')}
                                className="flex-none w-[44vw] sm:w-[32vw] snap-start group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-stone-100 rounded-xl mb-2.5">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                    {product.badge && (
                                        <div className={`absolute top-2 left-2 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider rounded text-white ${getBadgeColor(product.badge)}`}>
                                            {product.badge}
                                        </div>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setWishlist(prev => ({ ...prev, [index]: !prev[index] }));
                                        }}
                                        className="absolute top-2 right-2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center active:scale-90 transition-all"
                                    >
                                        <Heart className={`w-3.5 h-3.5 ${wishlist[index] ? 'fill-stone-900 text-stone-900' : 'text-stone-500'}`} />
                                    </button>
                                    <button
                                        onClick={(e) => e.stopPropagation()}
                                        className="absolute bottom-2 right-2 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md active:scale-90 transition-transform"
                                    >
                                        <Plus className="w-4 h-4 text-stone-900" />
                                    </button>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-stone-900 text-[12px] sm:text-sm font-light leading-snug mb-0.5" style={{ fontFamily: 'Georgia, serif' }}>
                                        {product.name}
                                    </h3>
                                    <div className="flex items-center justify-center gap-1.5">
                                        <span className="text-stone-500 text-[12px] sm:text-sm">{product.price}</span>
                                        {product.originalPrice && (
                                            <span className="text-stone-400 line-through text-[10px]">{product.originalPrice}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="absolute top-0 right-0 bottom-2 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                </div>

                {/* Mobile "View All" button */}
                <div className="mt-8 px-5">
                    <button
                        onClick={() => navigate('/template3/shop')}
                        className="w-full inline-flex items-center justify-center gap-3 border border-stone-900 text-stone-900 px-5 py-3.5 min-h-[48px] text-[12px] tracking-widest uppercase hover:bg-stone-900 hover:text-white transition-all duration-300"
                    >
                        View All Products <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop/Tablet Products - Elegant product grid (ORIGINAL) */}
            <section className="hidden lg:block py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16">
                    <div className="text-center mb-16">
                        <p className="text-stone-500 tracking-[0.3em] uppercase text-xs mb-4">New Arrivals</p>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light" style={{ fontFamily: 'Georgia, serif' }}>
                            The Latest
                        </h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {[
                            { name: 'Silk Midi Dress', price: '\u20B924,999', image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=500&h=600&fit=crop' },
                            { name: 'Cashmere Blazer', price: '\u20B932,999', image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=500&h=600&fit=crop' },
                            { name: 'Leather Tote', price: '\u20B918,999', image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=500&h=600&fit=crop' },
                            { name: 'Pearl Earrings', price: '\u20B98,999', image: 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=500&h=600&fit=crop' },
                            { name: 'Wool Coat', price: '\u20B945,999', image: 'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=500&h=600&fit=crop' },
                            { name: 'Satin Blouse', price: '\u20B912,999', image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=600&fit=crop' },
                            { name: 'Heeled Sandals', price: '\u20B915,999', image: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=500&h=600&fit=crop' },
                            { name: 'Evening Clutch', price: '\u20B99,999', image: 'https://images.unsplash.com/photo-1566150905458-1bf1fc113f0d?w=500&h=600&fit=crop' }
                        ].map((product, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template3/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden mb-4 bg-stone-100">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    {/* Quick Shop overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <button className="bg-white text-stone-900 px-6 py-3 text-xs tracking-widest uppercase hover:bg-stone-100 transition-colors">
                                            Quick View
                                        </button>
                                    </div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-sm lg:text-base font-light mb-1 group-hover:text-stone-600 transition-colors" style={{ fontFamily: 'Georgia, serif' }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-stone-500 text-sm">{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template3/shop')}
                            className="inline-flex items-center gap-3 border border-stone-900 text-stone-900 px-8 py-4 text-sm tracking-widest uppercase hover:bg-stone-900 hover:text-white transition-all duration-300"
                        >
                            View All Products
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ============================================
                EDITORIAL BANNER - Full width statement (Desktop/Tablet only - ORIGINAL)
            ============================================ */}
            <section className="hidden lg:flex relative h-[70vh] lg:h-[80vh] items-center justify-center">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=1920&q=80"
                        alt="Editorial"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-stone-900/40"></div>
                </div>
                <div className="relative text-center text-white px-4 max-w-4xl">
                    <p className="tracking-[0.3em] uppercase text-sm mb-6 text-stone-200">The Art of Dressing</p>
                    <h2 className="text-3xl sm:text-4xl lg:text-6xl font-light leading-tight mb-8" style={{ fontFamily: 'Georgia, serif' }}>
                        Every piece tells a story of exceptional craftsmanship
                    </h2>
                    <button
                        onClick={() => navigate('/template3/about')}
                        className="inline-flex items-center gap-3 border border-white text-white px-8 py-4 text-sm tracking-widest uppercase hover:bg-white hover:text-stone-900 transition-all duration-300"
                    >
                        Discover Our Atelier
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* ============================================
                BRAND STORY
            ============================================ */}

            {/* Mobile Brand Story - Full-bleed stacked */}
            <section className="lg:hidden py-10 sm:py-16 bg-stone-50">
                <div className="aspect-[4/5] overflow-hidden sm:rounded-2xl sm:mx-6">
                    <img
                        src="https://images.unsplash.com/photo-1558171813-4c088753af8f?w=800&h=1000&fit=crop"
                        alt="Our Story"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="px-5 sm:px-6 pt-8">
                    <p className="text-stone-500 tracking-[0.15em] uppercase text-[11px] sm:text-xs mb-3">Our Heritage</p>
                    <h2 className="text-xl sm:text-2xl font-light mb-4 sm:mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                        A Legacy of Excellence
                    </h2>
                    <p className="text-stone-600 text-[14px] sm:text-base leading-relaxed mb-4 sm:mb-5">
                        Founded with a singular vision of redefining luxury fashion, our house has been synonymous with exceptional quality and timeless design for over two decades.
                    </p>
                    <p className="text-stone-600 text-[14px] sm:text-base leading-relaxed mb-6 sm:mb-8">
                        Each garment is a testament to our unwavering commitment to craftsmanship, using only the finest materials sourced from the world's most prestigious mills.
                    </p>
                    <button
                        onClick={() => navigate('/template3/about')}
                        className="inline-flex items-center gap-2 text-stone-900 text-[12px] sm:text-sm tracking-widest uppercase border-b border-stone-900 pb-1 hover:text-stone-600 hover:border-stone-600 transition-colors"
                    >
                        Read Our Story <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop/Tablet Brand Story - Our heritage (ORIGINAL) */}
            <section className="hidden lg:block py-16 lg:py-24 bg-stone-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        {/* Image */}
                        <div className="relative">
                            <div className="aspect-[4/5] overflow-hidden">
                                <img
                                    src="https://images.unsplash.com/photo-1558171813-4c088753af8f?w=800&h=1000&fit=crop"
                                    alt="Our Story"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            {/* Decorative element */}
                            <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-stone-300 -z-10 hidden lg:block"></div>
                        </div>

                        {/* Content */}
                        <div className="lg:pl-8">
                            <p className="text-stone-500 tracking-[0.3em] uppercase text-xs mb-4">Our Heritage</p>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-8 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                                A Legacy of Excellence
                            </h2>
                            <div className="space-y-6 text-stone-600 leading-relaxed">
                                <p>
                                    Founded with a singular vision of redefining luxury fashion, our house has been synonymous with exceptional quality and timeless design for over two decades.
                                </p>
                                <p>
                                    Each garment is a testament to our unwavering commitment to craftsmanship, using only the finest materials sourced from the world's most prestigious mills.
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/template3/about')}
                                className="inline-flex items-center gap-2 text-stone-900 text-sm tracking-widest uppercase border-b border-stone-900 pb-1 mt-8 hover:text-stone-600 hover:border-stone-600 transition-colors"
                            >
                                Read Our Story <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================================
                SERVICES / FEATURES
            ============================================ */}

            {/* Mobile Features - Horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16 bg-stone-900 text-white">
                <div className="text-center px-5 sm:px-6 mb-8 sm:mb-12">
                    <p className="text-stone-400 tracking-[0.15em] uppercase text-[11px] sm:text-xs mb-3">The Experience</p>
                    <h2 className="text-xl sm:text-2xl font-light" style={{ fontFamily: 'Georgia, serif' }}>
                        Exceptional Service
                    </h2>
                </div>
                <div className="flex overflow-x-auto gap-6 px-5 snap-x snap-mandatory scrollbar-hide">
                    {features.map((feature, index) => (
                        <div key={index} className="flex-none w-[55vw] sm:w-[35vw] snap-start text-center py-2">
                            <div className="inline-flex items-center justify-center w-12 h-12 border border-stone-700 rounded-full mb-4">
                                <feature.icon className="w-5 h-5 text-stone-300" />
                            </div>
                            <h3 className="text-white text-[13px] font-light mb-1" style={{ fontFamily: 'Georgia, serif' }}>{feature.title}</h3>
                            <p className="text-stone-400 text-[11px]">{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Desktop/Tablet Services - What we offer (ORIGINAL) */}
            <section className="hidden lg:block py-16 lg:py-24 bg-stone-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16">
                    <div className="text-center mb-16">
                        <p className="text-stone-400 tracking-[0.3em] uppercase text-xs mb-4">The Experience</p>
                        <h2 className="text-3xl sm:text-4xl font-light" style={{ fontFamily: 'Georgia, serif' }}>
                            Exceptional Service
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                        {[
                            { icon: Sparkles, title: 'Personal Styling', desc: 'One-on-one consultations with our expert stylists' },
                            { icon: Award, title: 'Premium Quality', desc: 'Only the finest materials and craftsmanship' },
                            { icon: Gem, title: 'Exclusive Access', desc: 'First access to new collections and limited editions' },
                            { icon: Star, title: 'White Glove Delivery', desc: 'Complimentary luxury packaging and delivery' }
                        ].map((service, index) => (
                            <div key={index} className="text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 border border-stone-700 rounded-full mb-6">
                                    <service.icon className="w-7 h-7 text-stone-300" />
                                </div>
                                <h3 className="text-lg font-light mb-3" style={{ fontFamily: 'Georgia, serif' }}>{service.title}</h3>
                                <p className="text-stone-400 text-sm leading-relaxed">{service.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                TESTIMONIALS
            ============================================ */}

            {/* Mobile Testimonials - Swipeable carousel */}
            <section className="lg:hidden py-10 sm:py-16 bg-white">
                <div className="px-5 sm:px-6">
                    <div className="text-center mb-8 sm:mb-12">
                        <p className="text-stone-500 tracking-[0.15em] uppercase text-[11px] sm:text-xs mb-3">Client Words</p>
                        <h2 className="text-xl sm:text-2xl font-light" style={{ fontFamily: 'Georgia, serif' }}>
                            Testimonials
                        </h2>
                    </div>

                    <div
                        className="relative overflow-hidden"
                        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                        onTouchEnd={handleSwipe}
                    >
                        <div
                            className="flex transition-transform duration-400 ease-out"
                            style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                        >
                            {testimonials.map((t, index) => (
                                <div key={index} className="w-full flex-none px-2">
                                    <div className="bg-stone-50 rounded-2xl p-6 sm:p-8 text-center">
                                        <div className="flex justify-center gap-1 mb-4">
                                            {[...Array(t.rating)].map((_, i) => (
                                                <Star key={i} className="w-4 h-4 fill-stone-900 text-stone-900" />
                                            ))}
                                        </div>
                                        <p className="text-stone-700 text-[14px] leading-relaxed mb-5 italic" style={{ fontFamily: 'Georgia, serif' }}>
                                            "{t.text}"
                                        </p>
                                        <p className="text-stone-900 font-medium text-sm">{t.name}</p>
                                        <p className="text-stone-400 text-[11px] mt-0.5">{t.location}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-center gap-2 mt-5">
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setActiveTestimonial(index)}
                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                    index === activeTestimonial ? 'w-6 bg-stone-900' : 'w-1.5 bg-stone-300'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop/Tablet Testimonial - Client words (ORIGINAL) */}
            <section className="hidden lg:block py-20 lg:py-32 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="flex justify-center gap-1 mb-8">
                        {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-5 h-5 fill-stone-900 text-stone-900" />
                        ))}
                    </div>
                    <blockquote className="text-2xl sm:text-3xl lg:text-4xl font-light leading-relaxed mb-8 text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>
                        "The attention to detail and quality of every piece I've purchased has been extraordinary. This is what true luxury fashion should be."
                    </blockquote>
                    <div>
                        <p className="font-medium text-stone-900">Alexandra Chen</p>
                        <p className="text-stone-500 text-sm">Fashion Editor, Vogue</p>
                    </div>
                </div>
            </section>

            {/* ============================================
                NEWSLETTER
            ============================================ */}

            {/* Mobile Newsletter - Stacked rounded */}
            <section className="lg:hidden py-10 sm:py-16 bg-stone-100">
                <div className="max-w-2xl mx-auto px-5 sm:px-6 text-center">
                    <div className="inline-flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                        <div className="w-6 sm:w-8 h-px bg-stone-400"></div>
                        <Gem className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-stone-500" />
                        <div className="w-6 sm:w-8 h-px bg-stone-400"></div>
                    </div>
                    <h2 className="text-xl sm:text-2xl font-light mb-3 sm:mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                        Join Our Circle
                    </h2>
                    <p className="text-stone-500 text-[14px] sm:text-base mb-6 sm:mb-8">
                        Be the first to receive exclusive updates, private sale invitations, and styling inspiration.
                    </p>
                    <form className="flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Your email address"
                            className="w-full flex-1 px-5 py-3.5 border border-stone-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-900 transition-colors text-center sm:text-left text-[14px] sm:text-base rounded-xl sm:rounded-none"
                        />
                        <button
                            type="submit"
                            className="w-full sm:w-auto px-8 py-3.5 min-h-[48px] bg-stone-900 text-white text-[12px] sm:text-sm tracking-widest uppercase hover:bg-stone-800 transition-colors rounded-xl sm:rounded-none"
                        >
                            Subscribe
                        </button>
                    </form>
                    <p className="text-stone-400 text-[11px] sm:text-xs mt-4 tracking-wide">
                        We respect your privacy. Unsubscribe at any time.
                    </p>
                </div>
            </section>

            {/* Desktop/Tablet Newsletter - Elegant signup (ORIGINAL) */}
            <section className="hidden lg:block py-16 lg:py-24 bg-stone-100">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-3 mb-6">
                        <div className="w-8 h-px bg-stone-400"></div>
                        <Gem className="w-4 h-4 text-stone-500" />
                        <div className="w-8 h-px bg-stone-400"></div>
                    </div>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                        Join Our Circle
                    </h2>
                    <p className="text-stone-500 mb-8">
                        Be the first to receive exclusive updates, private sale invitations, and styling inspiration.
                    </p>
                    <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Your email address"
                            className="flex-1 px-6 py-4 border border-stone-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-900 transition-colors text-center sm:text-left"
                        />
                        <button
                            type="submit"
                            className="px-8 py-4 bg-stone-900 text-white text-sm tracking-widest uppercase hover:bg-stone-800 transition-colors"
                        >
                            Subscribe
                        </button>
                    </form>
                    <p className="text-stone-400 text-xs mt-4 tracking-wide">
                        We respect your privacy. Unsubscribe at any time.
                    </p>
                </div>
            </section>

            {/* ============================================
                INSTAGRAM - Social elegance (shared responsive grid)
            ============================================ */}
            <section className="py-10 sm:py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-6 sm:mb-10 lg:mb-12">
                        <p className="text-stone-500 tracking-[0.15em] sm:tracking-[0.3em] uppercase text-[11px] sm:text-xs mb-1 sm:mb-2">Follow Us</p>
                        <p className="text-stone-900 text-base sm:text-lg" style={{ fontFamily: 'Georgia, serif' }}>@prestigefashion</p>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-0.5 sm:gap-1 px-0 sm:px-6 lg:px-0">
                        {[
                            'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400&h=400&fit=crop',
                            'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=400&h=400&fit=crop',
                            'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=400&h=400&fit=crop',
                            'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400&h=400&fit=crop',
                            'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&h=400&fit=crop',
                            'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=400&fit=crop'
                        ].map((image, index) => (
                            <div key={index} className="relative aspect-square overflow-hidden group cursor-pointer">
                                <img src={image} alt={`Instagram ${index + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/30 transition-colors duration-300 flex items-center justify-center">
                                    <svg className="w-5 h-5 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default Template3;
